import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getNavbarMenu } from './config';
import { useTranslation } from 'react-i18next';
import { TopArrowIcon } from '../common/icon';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '@/apis/axiosInstance';
import { Skeleton, Tooltip } from 'antd';
import { useTimModalStore } from '@/tim/useTimModalStore';
import { useAuth } from '@clerk/clerk-react';
import AvatarPanel from './AvatarPanel';
import { useInvitesStore } from '@/hooks/useInvitesStore';
import { useInfluencerInfo } from '@/hooks/useInfluencerInfo';
import { Flex, Progress } from 'antd';
import { usePlatformStore } from '@/hooks/usePlatformStore';
import { CheckedIcon } from '../common/icon';
import ModalAfterRelease from '../web/components/ModalAfterRelease';
import { isFromApp } from '@/biz/tool';
import DownloadAppTextButton from './DownloadAppTextButton';

const LEADERBOARD_NEW_BADGE_FIRST_SEEN_AT = 'leaderboard_nav_new_first_seen_at';
const LEADERBOARD_NEW_BADGE_CLICKED_AT = 'leaderboard_nav_new_clicked_at';
const LEGACY_LEADERBOARD_NEW_BADGE_SEEN_AT = 'leaderboard_nav_new_seen_at';
const LEADERBOARD_NEW_BADGE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export default function NavbarContent({
  setMenuOpen,
  collapsed
}: {
  setMenuOpen?: (open: boolean) => void;
  collapsed?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = usePlatformStore();
  const { isLoaded, isSignedIn } = useAuth();
  const { influencerInfo } = useInfluencerInfo();
  const { referralInfo }: any = influencerInfo || {};
  const [openModalAfterRelease, setOpenModalAfterRelease] = useState(false);
  const { timUnreadCount, setShowTimModal } = useTimModalStore();
  const menu = useMemo(() => getNavbarMenu(t), [t]);
  const [showLeaderboardNewBadge, setShowLeaderboardNewBadge] = useState(() => {
    const now = Date.now();
    const clickedAt = Number(
      window.localStorage.getItem(LEADERBOARD_NEW_BADGE_CLICKED_AT) ||
        window.localStorage.getItem(LEGACY_LEADERBOARD_NEW_BADGE_SEEN_AT) ||
        0
    );
    if (clickedAt) return false;

    const firstSeenAt = Number(
      window.localStorage.getItem(LEADERBOARD_NEW_BADGE_FIRST_SEEN_AT) || 0
    );
    if (!firstSeenAt) {
      window.localStorage.setItem(
        LEADERBOARD_NEW_BADGE_FIRST_SEEN_AT,
        String(now)
      );
      return true;
    }
    return now - firstSeenAt < LEADERBOARD_NEW_BADGE_TTL_MS;
  });

  const stepsLists = [
    {
      title: t('components.set_onboarding_prompt.start_earning_1', {
        defaultValue: 'Take the platform tour'
      }),
      costTime: '10 min',
      todoKey: 'takeTheTour',
      doneState: user?.hasLearnedTheRopes
    },
    {
      title: t('components.set_onboarding_prompt.start_earning_2', {
        defaultValue: 'Set your rates'
      }),
      costTime: '2 min',
      todoKey: 'setMyRates',
      doneState: user?.isBaselineSet
    },
    {
      title: t('components.set_onboarding_prompt.start_earning_3', {
        defaultValue: 'Set up notifications'
      }),
      costTime: '1 min',
      todoKey: 'setNotifications',
      doneState: user?.isContactSet
    },
    {
      title: t('components.set_onboarding_prompt.start_earning_4', {
        defaultValue: 'Verify your channels'
      }),
      costTime: '1 min',
      todoKey: 'verifyNow',
      doneState: user?.isSocialConnected
    }
  ];
  const stepsDataReady = stepsLists.every(
    (step) => typeof step.doneState === 'boolean'
  );

  const toStep = (stepKey: string) => {
    const goToSettingsTab = (tab: string) => {
      if (location.pathname === '/settings') {
        window.location.hash = tab;
        return;
      }
      navigate(`/settings#${tab}`);
    };

    switch (stepKey) {
      case 'takeTheTour':
        setOpenModalAfterRelease(true);
        break;
      case 'setMyRates':
        goToSettingsTab('baselineRates');
        break;
      case 'setNotifications':
        goToSettingsTab('preferredContactInfo');
        break;
      case 'verifyNow':
        goToSettingsTab('connectSocials');
        break;
    }
  };

  const markLeaderboardSeen = () => {
    window.localStorage.setItem(
      LEADERBOARD_NEW_BADGE_CLICKED_AT,
      String(Date.now())
    );
    setShowLeaderboardNewBadge(false);
  };

  const isHrefActive = useCallback(
    (href?: string) => {
      if (!href) return false;
      const current = location.pathname;

      if (href === '/') return current === '/';
      const normalizedHref = href.endsWith('/') ? href.slice(0, -1) : href;
      if (current === normalizedHref) return true;
      return current.startsWith(`${normalizedHref}/`);
    },
    [location.pathname]
  );

  // 用于控制展开/收起的父级菜单 key（默认全部展开）
  const [openParentKeys, setOpenParentKeys] = useState<Set<string>>(() => {
    const parentKeys = menu
      .filter((item: any) => item.children?.length)
      .map((item: any) => item.key);
    return new Set(parentKeys);
  });

  // 路由命中某个子菜单时，确保对应父级至少保持展开
  useEffect(() => {
    const activeParent = menu.find((item: any) =>
      item.children?.some((child: any) => isHrefActive(child.href))
    );
    if (!activeParent?.key) return;

    setOpenParentKeys((prev) => {
      if (prev.has(activeParent.key)) return prev;
      const next = new Set(prev);
      next.add(activeParent.key);
      return next;
    });
  }, [location.pathname, menu, isHrefActive]);

  const parentIsActive = (item: any) =>
    item.children?.some((child: any) => isHrefActive(child.href)) ?? false;

  const handleReferral = () => {
    setMenuOpen?.(false);
    navigate('/referral');
  };

  // 获取达人端订单信息统计
  const [orderStats, setOrderStats] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const { invitesId } = useInvitesStore();
  const getOrderStatus = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `/influencer/orderstats?hash=${invitesId}`
      );
      setOrderStats(res?.data?.data || {});
    } catch (error) {
      console.error('getOrderStatus error: ', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setOrderStats({});
      setLoading(false);
      return;
    }
    getOrderStatus();
  }, [isLoaded, isSignedIn]);

  const handleMenuClick = (item: any) => {
    if (item.key === 'leaderboard') {
      markLeaderboardSeen();
    }
    if (item.key === 'message') {
      setShowTimModal(true);
    }
    setMenuOpen?.(false);
  };
  const shouldShowLeaderboardBadge = (item: any) =>
    item.key === 'leaderboard' && item.badge && showLeaderboardNewBadge;

  if (collapsed) {
    return (
      <div className="w-full flex flex-col flex-1 min-h-0 mt-4">
        <div className="flex flex-col items-center gap-2 flex-1 min-h-0 overflow-y-auto">
          {menu.map((item: any) => {
            const active =
              isHrefActive(item.href) ||
              (item.children?.length
                ? item.children.some((c: any) => isHrefActive(c.href))
                : false);
            const targetHref: string | undefined =
              item.href || item.children?.[0]?.href;

            if (item.key === 'message') {
              const Icon = item.icon;
              return (
                <Tooltip key={item.key} title={item.label} placement="right">
                  <button
                    type="button"
                    className={`relative flex items-center justify-center w-10 h-10 rounded-[6px] cursor-pointer transition-colors ${
                      active ? 'bg-[#FAFAF9]' : 'hover:bg-[#FAFAF9]'
                    }`}
                    aria-label={item.label}
                    onClick={() => handleMenuClick(item)}
                  >
                    <Icon
                      className={`w-5 h-5 ${
                        active ? 'text-[#292524]' : 'text-[#A9A29D]'
                      }`}
                    />
                    {timUnreadCount > 0 && (
                      <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-[#ff4d4f] rounded-full">
                        <div className="text-center text-white text-[10px] font-medium font-['DM_Sans'] leading-none">
                          {timUnreadCount > 99 ? '99+' : timUnreadCount}
                        </div>
                      </div>
                    )}
                  </button>
                </Tooltip>
              );
            }

            if (!targetHref) return null;

            const Icon = item.icon;
            return (
              <Tooltip key={item.key} title={item.label} placement="right">
                <button
                  type="button"
                  className={`relative flex items-center justify-center w-10 h-10 rounded-[6px] cursor-pointer transition-colors ${
                    active ? 'bg-[#FAFAF9]' : 'hover:bg-[#FAFAF9]'
                  }`}
                  aria-label={item.label}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => {
                    handleMenuClick(item);
                    navigate(targetHref);
                  }}
                >
                  <Icon
                    className={`w-5 h-5 ${
                      active ? 'text-[#292524]' : 'text-[#A9A29D]'
                    }`}
                  />
                  {shouldShowLeaderboardBadge(item) && (
                    <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-[#F04438]" />
                  )}
                </button>
              </Tooltip>
            );
          })}
        </div>
        <div className="flex-shrink-0 w-full flex justify-center pt-3 pb-1">
          <AvatarPanel setMenuOpen={setMenuOpen} compact />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col flex-1 min-h-0 mt-[20px] md:mt-4">
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-8 pr-0.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex flex-col gap-[2px]">
          {menu.map((item: any) => {
            const active = isHrefActive(item.href) || parentIsActive(item);
            const isOpen = openParentKeys.has(item.key);

            return (
              <React.Fragment key={item.key}>
                <div
                  className={`flex items-center justify-between h-[40px] px-3 rounded-[6px] cursor-pointer hover:bg-[#FAFAF9] ${
                    active ? 'bg-[#FAFAF9]' : ''
                  }`}
                  onClick={() => {
                    if (item.children?.length) {
                      setOpenParentKeys((prev) => {
                        const next = new Set(prev);
                        if (next.has(item.key)) next.delete(item.key);
                        else next.add(item.key);
                        return next;
                      });
                      return;
                    }
                    if (item.href) {
                      setMenuOpen?.(false);
                      navigate(item.href);
                    }
                    handleMenuClick?.(item);
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex gap-2 items-center">
                      <item.icon className="w-[20px] h-[20px] text-[#A9A29D]" />
                      <div
                        className={`justify-start text-base font-semibold font-['DM_Sans'] leading-6 ${
                          active ? '!text-[#292524]' : 'text-[#44403C]'
                        } hover:text-[#292524]`}
                      >
                        {item.label}
                      </div>
                      {shouldShowLeaderboardBadge(item) && (
                        <div className="rounded-full bg-[#F04438] px-1.5 py-0.5 text-[10px] font-black leading-none text-white">
                          {item.badge}
                        </div>
                      )}
                    </div>
                    {timUnreadCount > 0 && item.key === 'message' && (
                      <div className="min-w-[18px] h-[18px] rounded-full bg-[#ff4d4f] px-1 text-white text-[10px] font-medium flex items-center justify-center leading-none">
                        {timUnreadCount > 99 ? '99+' : timUnreadCount}
                      </div>
                    )}
                  </div>
                  {item.children?.length ? (
                    <div className="flex items-center">
                      <TopArrowIcon
                        className={`w-[20px] h-[20px] transition-transform duration-200 text-[#A9A29D] ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  ) : null}
                </div>

                {item.children?.length ? (
                  <div
                    className={`transition-all duration-200 ease-in-out overflow-hidden ${
                      isOpen ? 'max-h-[220px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="flex flex-col gap-[2px] pt-[2px]">
                      {item.children.map((child: any) => {
                        const childActive = isHrefActive(child.href);

                        return (
                          <div
                            key={child.key}
                            className={` pl-[40px] flex items-center justify-between h-[40px] px-3 rounded-[6px] cursor-pointer hover:bg-[#FAFAF9] ${
                              childActive ? 'bg-[#FAFAF9]' : ''
                            }`}
                            onClick={() => {
                              if (child.href) {
                                setMenuOpen?.(false);
                                navigate(child.href);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            <div className="flex gap-2 items-center">
                              <div
                                className={`justify-start text-base font-semibold font-['DM_Sans'] leading-6 hover:text-[#292524] ${
                                  childActive
                                    ? '!text-[#292524]'
                                    : 'text-[#44403C]'
                                }`}
                              >
                                {child.label}
                              </div>
                            </div>
                            {child?.key !== 'completed' && (
                              <>
                                {loading && (
                                  <Skeleton.Node
                                    active
                                    style={{
                                      width: 22,
                                      height: 22,
                                      borderRadius: '11px'
                                    }}
                                  />
                                )}
                                {!!orderStats?.[child?.key] && !loading && (
                                  <div className="px-2 h-[22px] flex items-center justify-center bg-[#FAFAF9] rounded-[11px] border border-[#E7E5E4]">
                                    <div className="text-center justify-start text-[#44403C] text-xs font-medium font-['DM_Sans'] leading-4">
                                      {orderStats?.[child?.key]}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <div className="flex-shrink-0 bg-white">
        {stepsDataReady && stepsLists?.some((step) => !step.doneState) && (
          <div className="w-full self-stretch p-4 bg-[#FAFAF9] mb-4 rounded-xl inline-flex flex-col justify-start items-start gap-3">
            <div className="self-stretch inline-flex justify-between items-center">
              <div className="flex-1 justify-start text-Colors-Text-text-primary-(900) text-sm font-semibold font-['DM_Sans'] leading-5">
                {t(
                  'components.set_onboarding_prompt.four_steps_to_start_earning',
                  {
                    defaultValue: '4 steps to start earning'
                  }
                )}{' '}
              </div>
              <div className="justify-start text-[#A6A09B] text-xs font-normal font-['DM_Sans'] leading-5">
                {stepsLists?.filter((step) => step.doneState).length}/4
              </div>
            </div>
            <Flex vertical gap="small" style={{ width: '100%' }}>
              <Progress
                percent={
                  (stepsLists?.filter((step) => step.doneState).length /
                    stepsLists.length) *
                  100
                }
                size="small"
                strokeColor="#FB6011"
                showInfo={false}
              />
            </Flex>
            <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
              {stepsLists.map((step, index) => (
                <div
                  key={index}
                  className="cursor-pointer self-stretch inline-flex justify-start items-center gap-1.5"
                  onClick={() => {
                    toStep(step.todoKey);
                    if (step?.todoKey !== 'takeTheTour') {
                      setMenuOpen?.(false);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  {step.doneState ? (
                    <div className="size-4 shrink-0 relative rounded-[999px] border border-[#009966] bg-[#009966] flex items-center justify-center">
                      <CheckedIcon className="w-3 h-3 text-white" />
                    </div>
                  ) : (
                    <div className="size-4 shrink-0 relative rounded-[999px] border border-Colors-Border-border-primary" />
                  )}
                  <div className="w-full flex justify-between items-center">
                    <div
                      className={`justify-start text-[#292524] text-xs font-normal font-['DM_Sans'] leading-5 ${step.doneState ? 'line-through text-[#A6A09B]' : ''}`}
                    >
                      {index + 1}. {step.title}
                    </div>
                    {!step.doneState && (
                      <div className="justify-start text-[#A6A09B] text-xs font-normal font-['DM_Sans'] leading-5">
                        {step.costTime}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {referralInfo?.inviteEnabled && (
          <div className="flex flex-col gap-3 md:gap-4">
            <button
              type="button"
              className="relative h-[100px] w-full overflow-hidden rounded-xl bg-[#FAFAF9] cursor-pointer"
              aria-label={t('navbar.referral.get_my_invite_link', {
                defaultValue: 'Get my invite link'
              })}
              onClick={handleReferral}
            >
              <img
                src="https://website-resource.ahaglobal.io/ads-platform/creator/referral-menu-home.png"
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-[14px]">
                <img
                  src="https://website-resource.ahaglobal.io/ads-platform/creator/invite-earn.png"
                  alt="Invite and Earn"
                  className="w-full"
                />
                <div className="text-center">
                  <span className="text-[#1C2024] text-sm font-normal font-['DM_Sans']">
                    {t('navbar.referral.earn_up_to', {
                      defaultValue: 'Refer creators and earn up to'
                    })}{' '}
                  </span>
                  <span className="text-[#1C2024] text-sm font-bold font-['DM_Sans']">
                    US$
                  </span>
                  <span className="text-[#1C2024] text-sm font-black font-['DM_Sans']">
                    10,000
                  </span>
                  <span className="text-[#1C2024] text-sm font-normal font-['DM_Sans']">
                    .{' '}
                  </span>
                </div>
              </div>
            </button>
          </div>
        )}
        {!isFromApp() && <DownloadAppTextButton />}
        <div className="mt-3">
          <AvatarPanel setMenuOpen={setMenuOpen} />
        </div>
        <ModalAfterRelease
          openModal={openModalAfterRelease}
          onCloseModal={() => setOpenModalAfterRelease(false)}
        />
      </div>
    </div>
  );
}
